import { EmailRequestType } from '@this+that/models';

import {
  fastForwardDraftVersion,
  forgetDraftVersions,
  getDraftHeadVersion,
  getLineageDraftId,
  getOwnConflictVersion,
  recordDraftWrite,
  recordLineageDraft,
} from './draftVersionLedger';
import {
  draftWriteKeys,
  enqueueDraftWrite,
  handOverWriteKey,
} from './draftWriteQueue';
import { isDraftNotFoundError } from '../../hooks/UserCommunications/draft/useDeleteDraft';
import {
  type EmailSendRequest,
  sendEmails,
} from '../../services/unifiedEmailAction.service';

export interface SendDraftWriteOptions {
  lineageId?: string;
  // Off for background sync: its content may be older than what the head holds.
  isOwnVersionTrusted?: boolean;
}

type SendEmailsResponse = Awaited<ReturnType<typeof sendEmails>>;

const recordResult = (
  request: EmailSendRequest,
  sentVersion: string | undefined,
  response: SendEmailsResponse,
): void => {
  const { messageId, draftVersion } = response.message ?? {};
  if (request.reqType === EmailRequestType.SEND_DRAFT && request.draftId) {
    forgetDraftVersions(request.draftId);
    return;
  }
  if (messageId && draftVersion) {
    recordDraftWrite(messageId, sentVersion, draftVersion);
  }
};

const sendWithVersion = async (
  request: EmailSendRequest,
  version: string | undefined,
): Promise<SendEmailsResponse> => {
  const response = await sendEmails({
    ...request,
    expectedDraftVersion: version,
  });
  recordResult(request, version, response);
  return response;
};

const sendTrustingOwnVersions = async (
  request: EmailSendRequest,
  draftId: string,
): Promise<SendEmailsResponse> => {
  const version = fastForwardDraftVersion(
    draftId,
    request.expectedDraftVersion,
  );
  try {
    return await sendWithVersion(request, version);
  } catch (error) {
    const ownVersion = getOwnConflictVersion(draftId, error, version);
    if (!ownVersion) {
      throw error;
    }
    return sendWithVersion(request, ownVersion);
  }
};

const runWrite = (
  request: EmailSendRequest,
  isOwnVersionTrusted: boolean,
): Promise<SendEmailsResponse> =>
  isOwnVersionTrusted && request.draftId
    ? sendTrustingOwnVersions(request, request.draftId)
    : sendWithVersion(request, request.expectedDraftVersion);

const isDraftCreate = (request: EmailSendRequest): boolean =>
  request.reqType === EmailRequestType.DRAFT && !request.draftId;

// A create queued behind another session's create of the same lineage must update what that one made.
const redirectToLineageDraft = (
  request: EmailSendRequest,
  lineageId: string | undefined,
): EmailSendRequest => {
  const draftId =
    lineageId && isDraftCreate(request)
      ? getLineageDraftId(lineageId)
      : undefined;
  return draftId
    ? {
        ...request,
        draftId,
        expectedDraftVersion: getDraftHeadVersion(draftId),
      }
    : request;
};

const settleCreate = (
  request: EmailSendRequest,
  lineageId: string | undefined,
  response: SendEmailsResponse,
): void => {
  const createdId = response.message?.messageId;
  if (request.draftId || !createdId) {
    return;
  }
  handOverWriteKey({ lineageId }, { draftId: createdId });
  if (lineageId && isDraftCreate(request)) {
    recordLineageDraft(lineageId, createdId);
  }
};

// The draft the lineage pointed to is gone: the next write of the lineage creates again.
const forgetRedirectIfGone = (
  error: unknown,
  redirectedDraftId: string | undefined,
): void => {
  if (redirectedDraftId && isDraftNotFoundError(error)) {
    forgetDraftVersions(redirectedDraftId);
  }
};

// Every draft write of the tab goes through here: queued per draft, with the version chain this tab produced.
export const sendDraftWrite = (
  request: EmailSendRequest,
  { lineageId, isOwnVersionTrusted = true }: SendDraftWriteOptions = {},
): Promise<SendEmailsResponse> =>
  enqueueDraftWrite(
    draftWriteKeys({ draftId: request.draftId, lineageId }),
    async () => {
      const queued = redirectToLineageDraft(request, lineageId);
      const isRedirected = queued !== request;
      try {
        const response = await runWrite(
          queued,
          isOwnVersionTrusted || isRedirected,
        );
        settleCreate(queued, lineageId, response);
        return response;
      } catch (error) {
        forgetRedirectIfGone(error, isRedirected ? queued.draftId : undefined);
        throw error;
      }
    },
  );
